import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import {createStoreClient} from "./StoreWebsocketService";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사(쇼핑몰) 전용 웹소켓 컨텍스트
 * ════════════════════════════════════════════════════════════════════════════════
 *  기존 WebsocketContext.js 와 파일/함수명을 완전히 분리했다.
 *  (회원·관리자 채팅은 기존 컨텍스트를 그대로 사용하므로 서로 영향이 없다)
 *
 *  [기존 WebsocketContext.js 에서 발견된 문제와 그 해결]
 *
 *   ① 구독목록을 useState 로 들고 있어 cleanup 이 '스테일 클로저' 를 참조했다.
 *      구독 직후 언마운트되면 cleanup 시점의 subscriptions 가 아직 {} 이라
 *      unsubscribe 가 아무것도 하지 않고 구독이 누수됐다.
 *      -> 구독목록을 useRef(Map) 로 관리한다. 항상 최신값이라 누수가 없다.
 *
 *   ② 구독을 컴포넌트가 '연결 이후에 직접' 호출해야 해서,
 *      연결완료 시점과 구독시점이 어긋나면 조용히 실패했다.
 *      -> 이 컨텍스트는 '구독 의사(wanted)' 를 먼저 등록받고,
 *         연결이 완료되는 순간 등록된 목록을 컨텍스트가 스스로 구독한다.
 *         재연결 후에도 자동으로 다시 구독한다.
 *
 *   ③ setInterval 재연결 루프가 stompjs 내장 재연결과 겹쳐 커넥션이 중복 생성됐다.
 *      -> 재연결은 stompjs(reconnectDelay)에만 맡긴다.
 *
 *   ④ client.webSocket.onclose 를 덮어써 stompjs 내부 정리로직을 망가뜨렸다.
 *      -> 공식 콜백 onWebSocketClose 만 사용한다.
 *
 *   ⑤ 커넥션이 전역으로 하나뿐이라 구독자가 모두 사라져도 연결이 남았다.
 *      -> 구독자 수를 세어 0 이 되면 연결을 정리한다. (구독자가 생기면 다시 연결)
 * ════════════════════════════════════════════════════════════════════════════════
 */

const StoreWebsocketContext = createContext(null);

export const StoreWebsocketProvider = ({children}) => {

    /** STOMP 클라이언트 (연결이 필요할 때만 생성된다) */
    const clientRef = useRef(null);
    /** 현재 커넥션의 CONNECT key */
    const keyRef = useRef(null);
    /** 구독 의사 : destination -> Set<handler> */
    const wantedRef = useRef(new Map());
    /** 실제 STOMP 구독객체 : destination -> subscription */
    const activeRef = useRef(new Map());

    const [storeConnected, setStoreConnected] = useState(false);

    // ── 실제 구독 반영 ──────────────────────────────────────────────────────────
    /**
     * wantedRef 에 등록된 목적지 중 아직 구독되지 않은 것을 구독한다.
     * 연결완료 직후 / 구독요청 시점 양쪽에서 호출되며, 몇 번 호출해도 안전하다.
     */
    const syncSubscriptions = useCallback(() => {
        const client = clientRef.current;
        if (!client || !client.connected) return;

        wantedRef.current.forEach((handlers, destination) => {
            if (activeRef.current.has(destination)) return;
            try {
                const sub = client.subscribe(destination, (frame) => {
                    // 한 목적지에 여러 컴포넌트가 붙어 있어도 모두에게 전달한다.
                    const current = wantedRef.current.get(destination);
                    if (!current) return;
                    current.forEach((fn) => {
                        try {
                            fn(frame);
                        } catch (e) {
                            console.error("[상점사 웹소켓] 수신 처리 중 오류", destination, e);
                        }
                    });
                });
                activeRef.current.set(destination, sub);
            } catch (e) {
                console.error("[상점사 웹소켓] 구독 실패", destination, e);
            }
        });
    }, []);

    /** 끊겼을 때 : 구독객체만 비운다. (구독 의사는 유지해야 재연결 후 복구된다) */
    const clearActiveSubscriptions = useCallback(() => {
        activeRef.current.clear();
    }, []);

    // ── 연결 / 해제 ────────────────────────────────────────────────────────────
    const ensureConnected = useCallback((storeKey) => {
        /*
         * 커넥션은 이 컨텍스트 전체에서 '하나' 만 유지한다.
         *
         * 상점사 채팅이 쓰는 목적지는 모두 /topic/... (브로드캐스트) 이고
         * 백엔드는 CONNECT 의 key 헤더로 구독을 분기하지 않는다.
         * 따라서 key 는 식별/디버깅용이며, 먼저 연결한 쪽의 key 를 그대로 유지한다.
         *
         * (관리자 [상점사] 탭은 'adminStoreChat', 그 안의 대화보드는 `store-{번호}` 로
         *  서로 다른 key 를 넘기는데, key 가 다르다고 재연결하면 상점을 고를 때마다
         *  커넥션이 끊겼다 붙으면서 수신이 잠깐씩 비게 된다)
         */
        if (clientRef.current) return;

        const client = createStoreClient(storeKey);

        client.onConnect = () => {
            setStoreConnected(true);
            clearActiveSubscriptions();  // 재연결인 경우 이전 구독객체는 무효
            syncSubscriptions();
        };

        client.onWebSocketClose = () => {
            setStoreConnected(false);
            clearActiveSubscriptions();  // stompjs 가 reconnectDelay 후 자동 재연결한다
        };

        client.onStompError = (frame) => {
            console.error("[상점사 웹소켓] STOMP 오류", frame?.headers?.message, frame?.body);
        };

        clientRef.current = client;
        keyRef.current = String(storeKey);

        try {
            client.activate();
        } catch (e) {
            console.error("[상점사 웹소켓] 연결 시작 실패", e);
        }
    }, [clearActiveSubscriptions, syncSubscriptions]);

    const teardown = useCallback(() => {
        const client = clientRef.current;
        clientRef.current = null;
        keyRef.current = null;
        clearActiveSubscriptions();
        setStoreConnected(false);
        if (!client) return;
        try {
            client.deactivate();
        } catch (e) {
            /* 정리 실패는 무시 */
        }
    }, [clearActiveSubscriptions]);

    // ── 외부 공개 API ──────────────────────────────────────────────────────────
    /**
     * 상점사 채팅 실시간 구독
     *
     *  연결이 아직 안 되어 있으면 연결부터 시작하고, 연결이 완료되는 순간
     *  컨텍스트가 알아서 구독한다. 호출측은 연결여부를 신경쓸 필요가 없다.
     *
     *  @param storeKey    CONNECT 식별값 (예: `store-12`)
     *  @param destination 구독 목적지 (예: `/topic/storeChat/12`)
     *  @param handler     수신 콜백 (frame)
     *  @return 구독해제 함수
     */
    const storeSubscribe = useCallback((storeKey, destination, handler) => {
        if (!storeKey || !destination || typeof handler !== 'function') return () => {
        };

        const handlers = wantedRef.current.get(destination) || new Set();
        handlers.add(handler);
        wantedRef.current.set(destination, handlers);

        ensureConnected(storeKey);
        syncSubscriptions();

        // 구독해제
        return () => {
            const current = wantedRef.current.get(destination);
            if (current) {
                current.delete(handler);
                if (current.size === 0) {
                    wantedRef.current.delete(destination);
                    const sub = activeRef.current.get(destination);
                    activeRef.current.delete(destination);
                    try {
                        sub?.unsubscribe();
                    } catch (e) {
                        /* 이미 끊긴 경우 무시 */
                    }
                }
            }
            // 남은 구독자가 없으면 커넥션까지 정리한다.
            if (wantedRef.current.size === 0) teardown();
        };
    }, [ensureConnected, syncSubscriptions, teardown]);

    // 연결상태가 바뀌면(특히 재연결 성공) 밀린 구독을 반영한다.
    useEffect(() => {
        if (storeConnected) syncSubscriptions();
    }, [storeConnected, syncSubscriptions]);

    // 앱 종료 시 정리
    useEffect(() => {
        return () => teardown();
    }, [teardown]);

    return (
        <StoreWebsocketContext.Provider value={{storeConnected, storeSubscribe}}>
            {children}
        </StoreWebsocketContext.Provider>
    );
};

/**
 * 상점사 웹소켓 훅
 *  - Provider 밖에서 호출되어도 앱이 죽지 않도록 안전한 기본값을 돌려준다.
 */
export const useStoreWebSocket = () => {
    const ctx = useContext(StoreWebsocketContext);
    if (!ctx) {
        return {
            storeConnected: false, storeSubscribe: () => () => {
            }
        };
    }
    return ctx;
};
